import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Flag, MessageCircle, TrendingUp, Clock, Shield,
  UserCheck, UserX, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick }) => (
  <button
    onClick={onClick}
    className={`bg-[#111] border border-white/10 rounded-xl p-5 text-left hover:border-${color}/50 transition-colors w-full`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[#A1A1AA] text-sm">{title}</p>
        <p className="text-3xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-xs text-[#52525B] mt-1">{subtitle}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
    </div>
  </button>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentReports, setRecentReports] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/reports?limit=5&status=pending`)
      ]);
      setStats(statsRes.data);
      setRecentReports(reportsRes.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-[#A1A1AA]">Overview of your platform metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.users?.total || 0}
          subtitle={`${stats?.users?.new_today || 0} new today`}
          icon={Users}
          color="[#08D9D6]"
          onClick={() => navigate('/admin/users')}
        />
        <StatCard
          title="Online Now"
          value={stats?.users?.online_now || 0}
          subtitle="Active in last 15 min"
          icon={Clock}
          color="[#22c55e]"
        />
        <StatCard
          title="Pending Reports"
          value={stats?.reports?.pending || 0}
          subtitle={`${stats?.reports?.today || 0} new today`}
          icon={Flag}
          color="[#FF2E63]"
          onClick={() => navigate('/admin/reports')}
        />
        <StatCard
          title="Messages Today"
          value={stats?.engagement?.messages_today || 0}
          subtitle={`${stats?.engagement?.messages_this_week || 0} this week`}
          icon={MessageCircle}
          color="[#7000FF]"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <UserCheck className="w-5 h-5 text-[#22c55e]" />
            <span className="text-[#A1A1AA] font-medium">Verified Users</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.users?.verified || 0}</p>
          <p className="text-xs text-[#52525B] mt-1">
            {stats?.users?.total ? Math.round((stats.users.verified / stats.users.total) * 100) : 0}% of total
          </p>
        </div>

        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <span className="text-[#A1A1AA] font-medium">Suspended</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.users?.suspended || 0}</p>
          <p className="text-xs text-[#52525B] mt-1">Temporary restrictions</p>
        </div>

        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <UserX className="w-5 h-5 text-red-500" />
            <span className="text-[#A1A1AA] font-medium">Banned</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.users?.banned || 0}</p>
          <p className="text-xs text-[#52525B] mt-1">Permanent bans</p>
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-[#111] border border-white/10 rounded-xl">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Pending Reports</h2>
            <p className="text-sm text-[#A1A1AA]">Requires your attention</p>
          </div>
          <button
            onClick={() => navigate('/admin/reports')}
            className="text-sm text-[#FF2E63] hover:underline"
          >
            View All
          </button>
        </div>

        {recentReports.length === 0 ? (
          <div className="p-8 text-center">
            <Shield className="w-12 h-12 text-[#22c55e] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No pending reports</p>
            <p className="text-sm text-[#52525B]">All caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {recentReports.map((report) => (
              <button
                key={report.id}
                onClick={() => navigate(`/admin/reports/${report.id}`)}
                className="w-full p-4 hover:bg-white/5 transition-colors text-left flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                    <Flag className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {report.reported_user?.display_name || report.reported_user?.username || 'Unknown User'}
                    </p>
                    <p className="text-sm text-[#A1A1AA] truncate max-w-xs">
                      {report.reason}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="px-2 py-1 rounded text-xs bg-yellow-500/10 text-yellow-500">
                    {report.status}
                  </span>
                  <p className="text-xs text-[#52525B] mt-1">
                    {new Date(report.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Growth Stats */}
      <div className="bg-[#111] border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-5 h-5 text-[#08D9D6]" />
          <span className="text-white font-semibold">User Growth</span>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-white">{stats?.users?.new_today || 0}</p>
            <p className="text-xs text-[#A1A1AA]">Today</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{stats?.users?.new_this_week || 0}</p>
            <p className="text-xs text-[#A1A1AA]">This Week</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{stats?.users?.new_this_month || 0}</p>
            <p className="text-xs text-[#A1A1AA]">This Month</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
